import React, {
    MouseEventHandler,
    KeyboardEventHandler,
    FocusEventHandler,
    FC,
    MutableRefObject,
} from 'react';
import classnames from 'classnames';
import styles from './main.scss';

type BuyerLinkType =
    | 'standardLink'
    | 'invertedLink'
    | 'contrastLinkLightInverse'
    | 'contrastLinkLight'
    | 'contrastLinkAlternate'
    | 'contrastLinkNoEffect'
    | 'standardLinkNoEffect'
    | 'linkNoDecoration';

type Underline = 'none' | 'underline';

// only use Role if you know you need it, otherwise the implicit roles that
// button (role=button) and anchor (role=link) are fine
type Roles =
    | 'menuitem' // use if container has role="menu"
    | 'link' // use if no href but onClick navigates with window.location
    | 'tab'; // use in aria tab widget (dibs-elements Tabs)

type AnchorProps = { rel?: string; href?: string; target?: '_blank'; download?: boolean | string };

type AnchorExternalProps = Omit<AnchorProps, 'rel'>;

type NeverAnchorExternalProps = { [P in keyof AnchorExternalProps]?: never };

interface BaseProps {
    className?: string;
    dataTn?: string;
    buyerLinkType?: BuyerLinkType;
    underline?: Underline;
    id?: string;
    tabIndex?: number;
    // ref provider needs runtime checks for anchor or button if using
    // attributes specific to either
    linkRef?:
        | ((node: HTMLAnchorElement | HTMLButtonElement | null) => void)
        | MutableRefObject<HTMLAnchorElement | HTMLButtonElement>;
    // event handlers
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onBlur?: FocusEventHandler;
    onFocus?: FocusEventHandler;
    // a11y props
    ariaExpanded?: boolean;
    ariaHasPopup?: boolean;
    ariaSelected?: boolean;
    ariaLabel?: string;
    ariaControls?: string;
    ariaDescribedBy?: string;
    role?: Roles;
}
interface Anchor extends BaseProps, AnchorExternalProps {
    href: string;
    onClick?: MouseEventHandler;
}
interface Button extends BaseProps, NeverAnchorExternalProps {
    onClick: MouseEventHandler;
}

// discriminated union of two interfaces to ensure one of href OR onClick is
// required and to catch mismatched props (no download w/o href, etc.)

type LinkProps = Button | Anchor;

function isValidHref(href: string | undefined): boolean {
    if (!href) {
        // if href is empty string or undefined, href is not valid
        return false;
    } else if (href === '#') {
        // if href is empty url fragment, href is not valid
        return false;
    } else if (/^\W*?javascript:/.test(href)) {
        // if href contains address-bar-executable function, href is not valid
        //
        // most common is "javascript:void(0)", which returns undefined, doing nothing
        // when it executes. this is used bc it gives definition to href, allowing focus
        return false;
    } else {
        // valid href is string with non-empty value, is not empty fragment and is not
        // a function
        return true;
    }
}

export const Link: FC<LinkProps> = ({
    className,
    buyerLinkType,
    underline,
    dataTn,
    href,
    target,
    download,
    children,
    ariaExpanded,
    ariaHasPopup,
    ariaSelected,
    ariaDescribedBy,
    ariaControls,
    ariaLabel,
    linkRef,
    role,
    onClick,
    id,
    tabIndex,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    onBlur,
    onFocus,
}) => {
    const isAnchor = isValidHref(href);

    const classes = classnames(
        styles.link,
        underline && styles[underline],
        buyerLinkType && styles[buyerLinkType],
        className
    );
    if (isAnchor) {
        return (
            <a
                id={id}
                className={classes}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onBlur={onBlur}
                onFocus={onFocus}
                tabIndex={tabIndex}
                role={role}
                aria-expanded={ariaExpanded}
                aria-haspopup={ariaHasPopup}
                aria-selected={ariaSelected}
                aria-describedby={ariaDescribedBy}
                aria-label={ariaLabel}
                aria-controls={ariaControls}
                data-tn={dataTn}
                // anchor props
                rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                href={href}
                target={target}
                download={download}
                ref={linkRef as MutableRefObject<HTMLAnchorElement>}
            >
                {children}
            </a>
        );
    }

    return (
        <button
            id={id}
            className={classes}
            onClick={onClick}
            onKeyDown={onKeyDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onBlur={onBlur}
            onFocus={onFocus}
            tabIndex={tabIndex}
            role={role}
            aria-expanded={ariaExpanded}
            aria-haspopup={ariaHasPopup}
            aria-selected={ariaSelected}
            aria-describedby={ariaDescribedBy}
            aria-label={ariaLabel}
            aria-controls={ariaControls}
            data-tn={dataTn}
            // button props
            type="button"
            ref={linkRef as MutableRefObject<HTMLButtonElement>}
        >
            {children}
        </button>
    );
};
